import { Injectable } from '@angular/core';
import { openBlobInNewTab, downloadBlob } from '../../../utils/blob-viewer.util';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { ReportingActions } from '@store/actions/reporting/reporting.actions';
import { ReportingService } from '@services/reporting.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { selectReportContext } from '@store/selectors/reporting/reporting.selectors';
import { selectProviders as selectClinicianProviders } from '@store/selectors/clinician-details/clinician-details.selectors';
import { parseProblemDetailsError, parseProviderConflictError } from '../../../utils/api-error.util';

@Injectable()
export class ReportingEffects {

  viewReportTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.viewReportTemplate),
      exhaustMap(() =>
        this.reportingService.getPracticeSummaryTemplate().pipe(
          map((fileBlob) => ReportingActions.viewReportTemplateSuccess({ fileBlob })),
          catchError((error) =>
            of(ReportingActions.viewReportTemplateFailure({ error: error?.message ?? 'Failed to retrieve report template' }))
          )
        )
      )
    )
  );

  viewReportTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.viewReportTemplateSuccess),
        tap(({ fileBlob }) => openBlobInNewTab(fileBlob))
      ),
    { dispatch: false }
  );

  viewReportTemplateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.viewReportTemplateFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to retrieve the report template. Please try again.',
            'Report Error'
          );
        })
      ),
    { dispatch: false }
  );

  bulkRunReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.bulkRunReport),
      withLatestFrom(this.store.select(selectReportContext)),
      exhaustMap(([{ providers, emailRecipients }, context]) =>
        this.reportingService.bulkRunReport({
          organizationId: context.orgId,
          reportingYear: context.programYear,
          providers,
          emailRecipients,
        }).pipe(
          map((response) => {
            if (response.partialSuccess) {
              return ReportingActions.bulkRunReportPartialFailure({
                error: response.error ?? 'Some reports failed to queue',
                queuedProviderIds: response.queuedProviderIds,
                failedProviderIds: response.failedProviderIds,
              });
            }
            // API contract: partialSuccess:false means failedProviderIds is empty and all submitted providers were queued.
            return ReportingActions.bulkRunReportSuccess({ queuedProviderIds: response.queuedProviderIds });
          }),
          catchError((error: HttpErrorResponse) =>
            of(ReportingActions.bulkRunReportFailure({
              error: (error.error as { message?: string })?.message ?? error.message ?? 'Failed to queue reports',
            }))
          )
        )
      )
    )
  );

  bulkRunReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.bulkRunReportFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error ?? 'Failed to queue the reports. Please try again.',
            'Report Error'
          );
        })
      ),
    { dispatch: false }
  );

  bulkRunReportPartialFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.bulkRunReportPartialFailure),
        // Intentionally searches all providers (not just active) so names still resolve
        // if a provider was deactivated between request and response.
        withLatestFrom(this.store.select(selectClinicianProviders)),
        tap(([{ queuedProviderIds, failedProviderIds }, providers]) => {
          const resolveName = (id: string) => {
            const provider = providers.find((p) => String(p.id) === id);
            return provider ? `${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim() : id;
          };
          const failedNames = failedProviderIds.map(resolveName);
          const queuedCount = queuedProviderIds.length;
          let message: string;
          if (failedNames.length > 0) {
            const nameList = failedNames.length > 5
              ? `${failedNames.slice(0, 5).join(', ')} and ${failedNames.length - 5} more`
              : failedNames.join(', ');
            message = `Reports could not be queued for: ${nameList}. ${queuedCount} report${queuedCount === 1 ? '' : 's'} were successfully queued — check the results table.`;
          } else {
            message = `${queuedCount} report${queuedCount === 1 ? '' : 's'} were queued before an error occurred. Check the results table for reports that were processed.`;
          }
          this.errorDialogService.showError(message, 'Partial Failure');
        })
      ),
    { dispatch: false }
  );

  runReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.runReport),
      withLatestFrom(this.store.select(selectReportContext)),
      exhaustMap(([{ params }, context]) => {
        const providerId = params.providerId === 'Organization' ? undefined : Number(params.providerId);
        return this.reportingService.runReport({
          organizationId: context.orgId,
          providerId,
          reportingYear: context.programYear,
          emails: params.emails.length > 0 ? params.emails : undefined,
        }).pipe(
          map(({ blob, fileName }) =>
            ReportingActions.runReportSuccess({ fileBlob: blob, fileName })
          ),
          catchError((error) =>
            of(ReportingActions.runReportFailure({ error: error?.message ?? 'Failed to generate report' }))
          )
        );
      })
    )
  );

  runReportSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.runReportSuccess),
      tap(({ fileBlob, fileName }) => downloadBlob(fileBlob, fileName)),
      map(() => ReportingActions.loadReportResults()),
      catchError((_, caught) => caught)
    )
  );

  runReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.runReportFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to generate the report. Please try again.',
            'Report Error'
          );
        })
      ),
    { dispatch: false }
  );

  exportOrgAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.exportOrgAudit),
      exhaustMap(() =>
        this.reportingService.exportOrgAudit().pipe(
          map((fileBlob) =>
            ReportingActions.exportOrgAuditSuccess({
              fileBlob,
              fileName: `organization-audit-${new Date().toISOString().slice(0, 10)}.xlsx`,
            })
          ),
          catchError((error) =>
            of(ReportingActions.exportOrgAuditFailure({ error: error?.message ?? 'Failed to export organization audit' }))
          )
        )
      )
    )
  );

  exportOrgAuditSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.exportOrgAuditSuccess),
        tap(({ fileBlob, fileName }) => downloadBlob(fileBlob, fileName))
      ),
    { dispatch: false }
  );

  exportOrgAuditFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.exportOrgAuditFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to export the organization audit. Please try again.',
            'Export Error'
          );
        })
      ),
    { dispatch: false }
  );

  // Organizational Reports

  loadReportResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.loadReportResults),
      withLatestFrom(this.store.select(selectReportContext)),
      switchMap(([, context]) =>
        this.reportingService.getReportResults(context.orgId).pipe(
          map(({ results, totalCount }) =>
            ReportingActions.loadReportResultsSuccess({ results, totalCount })
          ),
          catchError((error) =>
            of(ReportingActions.loadReportResultsFailure({ error: error?.message ?? 'Failed to load report results' }))
          )
        )
      )
    )
  );

  viewReportResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.viewReportResult),
      exhaustMap(({ id }) =>
        this.reportingService.viewReportResult(id).pipe(
          map((fileBlob) => ReportingActions.viewReportResultSuccess({ fileBlob })),
          catchError((error) =>
            of(ReportingActions.viewReportResultFailure({ error: error?.message ?? 'Failed to retrieve report' }))
          )
        )
      )
    )
  );

  viewReportResultSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.viewReportResultSuccess),
        tap(({ fileBlob }) => openBlobInNewTab(fileBlob))
      ),
    { dispatch: false }
  );

  viewReportResultFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.viewReportResultFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to retrieve the report. Please try again.',
            'Report Error'
          );
        })
      ),
    { dispatch: false }
  );

  downloadReportResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.downloadReportResult),
      exhaustMap(({ id }) =>
        this.reportingService.downloadReportResult(id).pipe(
          map(({ blob, fileName }) =>
            ReportingActions.downloadReportResultSuccess({ fileBlob: blob, fileName })
          ),
          catchError((error) =>
            of(ReportingActions.downloadReportResultFailure({ error: error?.message ?? 'Failed to download report' }))
          )
        )
      )
    )
  );

  downloadReportResultSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.downloadReportResultSuccess),
        tap(({ fileBlob, fileName }) => downloadBlob(fileBlob, fileName))
      ),
    { dispatch: false }
  );

  downloadReportResultFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.downloadReportResultFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to download the report. Please try again.',
            'Download Error'
          );
        })
      ),
    { dispatch: false }
  );

  deleteReportResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.deleteReportResult),
      exhaustMap(({ id }) =>
        this.reportingService.deleteReportResult(id).pipe(
          map(() => ReportingActions.deleteReportResultSuccess({ id })),
          catchError((error) =>
            of(ReportingActions.deleteReportResultFailure({ error: error?.message ?? 'Failed to delete report' }))
          )
        )
      )
    )
  );

  deleteReportResultFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.deleteReportResultFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to delete the report. Please try again.',
            'Delete Error'
          );
        })
      ),
    { dispatch: false }
  );

  // Scheduled Reports

  loadSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.loadSchedules),
      withLatestFrom(this.store.select(selectReportContext)),
      switchMap(([, context]) =>
        this.reportingService.getSchedules(context.orgId).pipe(
          map(({ schedules, totalCount }) =>
            ReportingActions.loadSchedulesSuccess({ schedules, totalCount })
          ),
          catchError((error) =>
            of(ReportingActions.loadSchedulesFailure({ error: error?.message ?? 'Failed to load schedules' }))
          )
        )
      )
    )
  );

  createSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.createSchedule),
      withLatestFrom(this.store.select(selectReportContext)),
      exhaustMap(([{ request }, context]) =>
        this.reportingService.createSchedule({ ...request, organizationId: context.orgId }).pipe(
          map(({ schedules }) => ReportingActions.createScheduleSuccess({ schedules })),
          catchError((error: HttpErrorResponse) =>
            of(ReportingActions.createScheduleFailure({
              error: parseProblemDetailsError(error, 'Failed to create schedule'),
              isDuplicate: error?.status === 409,
            }))
          )
        )
      )
    )
  );

  createScheduleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.createScheduleFailure),
        tap(({ isDuplicate, error }) => {
          const parsed = isDuplicate && error ? parseProviderConflictError(error) : null;
          if (parsed) {
            this.errorDialogService.showCustom({
              title: 'Schedule Error',
              icon: 'error',
              confirmButtonText: 'OK',
              message: parsed.message,
              items: parsed.items,
            });
          } else {
            this.errorDialogService.showError(
              error ?? 'Failed to create the schedule. Please try again.',
              'Schedule Error'
            );
          }
        })
      ),
    { dispatch: false }
  );

  updateSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.updateSchedule),
      exhaustMap(({ id, request }) =>
        this.reportingService.updateSchedule(id, request).pipe(
          map((schedule) => ReportingActions.updateScheduleSuccess({ schedule })),
          catchError((error) =>
            of(ReportingActions.updateScheduleFailure({ error: error?.message ?? 'Failed to update schedule' }))
          )
        )
      )
    )
  );

  updateScheduleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.updateScheduleFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to update the schedule. Please try again.',
            'Schedule Error'
          );
        })
      ),
    { dispatch: false }
  );

  refreshSchedulesAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.createScheduleSuccess, ReportingActions.updateScheduleSuccess),
      map(() => ReportingActions.loadSchedules())
    )
  );

  deleteSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.deleteSchedule),
      exhaustMap(({ id }) =>
        this.reportingService.deleteSchedule(id).pipe(
          map(() => ReportingActions.deleteScheduleSuccess({ id })),
          catchError((error) =>
            of(ReportingActions.deleteScheduleFailure({ error: error?.message ?? 'Failed to delete schedule' }))
          )
        )
      )
    )
  );

  reactivateSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportingActions.reactivateSchedule),
      exhaustMap(({ id }) =>
        this.reportingService.reactivateSchedule(id).pipe(
          map((schedule) => ReportingActions.reactivateScheduleSuccess({ schedule })),
          catchError((error: HttpErrorResponse) =>
            of(ReportingActions.reactivateScheduleFailure({
              error: parseProblemDetailsError(error, 'Failed to reactivate schedule'),
              isDuplicate: error?.status === 409,
            }))
          )
        )
      )
    )
  );

  reactivateScheduleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.reactivateScheduleFailure),
        tap(({ isDuplicate, error }) => {
          const message = isDuplicate
            ? (error ?? 'An active schedule already exists for this report, cadence, and provider combination. Please edit the existing schedule instead.')
            : 'Failed to reactivate the schedule. Please try again.';
          this.errorDialogService.showError(message, 'Reactivate Error');
        })
      ),
    { dispatch: false }
  );

  deleteScheduleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportingActions.deleteScheduleFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to deactivate the schedule. Please try again.',
            'Deactivate Error'
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly reportingService: ReportingService,
    private readonly errorDialogService: ErrorDialogService,
  ) {}
}
